import Seo from "../components/Seo";
import PageHeader, { DetailRow } from "../components/PageHeader";
import CtaSection from "../components/CtaSection";
import { Reveal } from "../components/Reveal";

const TOPICS = [
  {
    title: "Income changes",
    body: "A new job, a business that took off, a spouse’s income change. When income shifts, questions follow. Planning conversations help you see the change coming and frame the right questions early.",
  },
  {
    title: "Self-employment",
    body: "Working for yourself changes how money reaches you. And how you should think about setting aside for taxes. We help you organize records and raise the right questions for your situation.",
  },
  {
    title: "Withholding questions",
    body: "Withholding that’s comfortably right one year can be off the next. We help you understand the moving parts and what to check when life changes.",
  },
  {
    title: "Family changes",
    body: "Marriage, a new child, a move, a household growing or shrinking. Family changes touch your personal tax picture. Planning means thinking about them before the year closes.",
  },
  {
    title: "Retirement transitions",
    body: "Approaching retirement changes income patterns, timing, and priorities. We help you get organized for the transition and coordinate the questions worth asking.",
  },
  {
    title: "Preparation readiness",
    body: "However simple or complex your situation, preparation is calmer when the year’s papers are gathered as you go. We’ll help you build a habit. See our printable personal tax checklist.",
  },
];

export default function TaxPersonal() {
  return (
    <>
      <Seo
        title="Personal Tax Planning | Directive Optive Solutions"
        description="Personal tax planning conversations: income changes, self-employment, withholding, family changes, and retirement transitions. Planned before the year closes."
        path="/tax-planning/personal"
      />
      <PageHeader
        eyebrow="Personal Tax Planning"
        title="Your household’s finances, thought through."
        lead="Life changes fast, and personal taxes follow. Planning conversations look at what’s changed and what’s coming. Income, family, retirement. So the year closes with fewer surprises."
        image="/img/photo-financial-partner.webp"
        imageAlt="A person planning family finances at a laptop"
      />

      <section className="py-24 lg:py-32" data-testid="personal-tax-topics-section">
        <div className="container-shell">
          <Reveal className="max-w-3xl">
            <h2 className="font-serif text-3xl text-olive sm:text-4xl">What we talk about</h2>
            <p className="mt-5 text-base leading-relaxed text-moss sm:text-lg">Planning topics, subject to confirmed service scope. Not promises of deductions, refunds, or savings, and not tax or legal advice.</p>
          </Reveal>
          <div className="mt-14 border-b border-line">
            {TOPICS.map((t, i) => (
              <DetailRow key={t.title} title={t.title} testId={`personal-tax-topic-${i + 1}`}>
                <p>{t.body}</p>
              </DetailRow>
            ))}
          </div>
          <Reveal className="mt-14 grid gap-6 md:grid-cols-2">
            <div className="rounded-md border border-olive/25 bg-sage p-8">
              <h3 className="font-serif text-xl text-olive">What to prepare</h3>
              <p className="mt-3 text-sm leading-relaxed text-moss">A simple picture of your income, a note of what changed this year, and your questions. Please don’t send tax documents or account numbers through this website. We’ll exchange anything sensitive through secure channels once engaged.</p>
            </div>
            <div className="rounded-md border border-olive/25 bg-sage p-8">
              <h3 className="font-serif text-xl text-olive">How to get started</h3>
              <p className="mt-3 text-sm leading-relaxed text-moss">Book a consultation online, call, or send the inquiry form and mention personal tax planning. The first conversation is about where you are. Nothing to have “ready” first.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaSection heading="Plan the year while it’s still the year." body="Book a personal tax planning conversation, or write to us about what’s changing in your household." />
    </>
  );
}
